/**
 * Transient notices: "el flujo quedó creado", "no fue posible archivar".
 *
 * Module-level state rather than a Pinia store or provide/inject: any
 * component may raise a toast, including one that is unmounting as a result of
 * the very action being reported (the archive dialog closes itself), so the
 * queue must outlive the caller's component instance. Plain exported functions
 * rather than a `useToasts()` hook, because there is nothing per-component to
 * set up and no reactive scope to bind to.
 */
import { ref, type Ref } from 'vue'

export type ToastTone = 'success' | 'error' | 'info'

export interface Toast {
  id: number
  tone: ToastTone
  message: string
}

/** Long enough to read a sentence, short enough not to stack up. */
const DISMISS_MS = 6000

const queue = ref<Toast[]>([])
let nextId = 1

/** The live queue, for `UiToast` to render. */
export const toasts: Readonly<Ref<Toast[]>> = queue

export function dismissToast(id: number): void {
  queue.value = queue.value.filter((toast) => toast.id !== id)
}

export function pushToast(tone: ToastTone, message: string): void {
  const id = nextId++
  queue.value = [...queue.value, { id, tone, message }]
  // An error stays until dismissed: it usually names something the user has to
  // do about it, and six seconds is not long enough to decide what.
  if (tone !== 'error') {
    window.setTimeout(() => dismissToast(id), DISMISS_MS)
  }
}
